import { forwardRef } from 'react';
import { useIsClientReady } from '@belong/common';
import { IconSpinner } from '@belong/icons';
import { Link } from '@belong/ui';
import clsx from 'clsx';
import {
  HomeAvailabilityStatus,
  isBlocked,
} from 'src/features/listing-page/availability';
import { useUser } from 'src/features/users/useUser';
import { useResidentApplications } from '../../queries';
import {
  getLabel,
  getHref,
  getCurrentApplication,
  getCurrentApplicationUserStatus,
} from './apply-button.helpers';

type ApplyButtonRef = HTMLAnchorElement;

type ApplyButtonProps = {
  homeId?: string;
  listingId?: string;
  isDemoUnit?: boolean;
  isAvailableForRent?: boolean;
  isComingSoon?: boolean;
  availabilityStatus?: HomeAvailabilityStatus;
  size: 'fluid' | 'small';
  className?: string;
};

export const ApplyButton = forwardRef<ApplyButtonRef, ApplyButtonProps>(
  function ApplyButton(
    {
      homeId = '',
      listingId = '',
      isDemoUnit,
      isAvailableForRent,
      isComingSoon,
      availabilityStatus,
      size,
      className,
    },
    ref,
  ): JSX.Element {
    const isClientReady = useIsClientReady();

    const { user, isLoadingUser } = useUser();
    const { applications = [], isLoadingApplications } =
      useResidentApplications(!user);

    const currentApplication = getCurrentApplication(
      homeId,
      listingId,
      applications,
    );
    const currentApplicationUserStatus = getCurrentApplicationUserStatus(
      user?.email,
      currentApplication,
    );

    // Coming Soon homes are pre-Full-Listing-Published, so isAvailableForRent is always false for
    // them, even though they're published and meant to accept applications (LOVE-335) — only a
    // truly unavailable (e.g. rented) home should fall back to "Loved".
    const blocked = isBlocked({
      availabilityStatus,
      isAvailableForRent,
      isComingSoon,
    });
    const canApply = (!!isAvailableForRent || !!isComingSoon) && !blocked;
    const label = getLabel(
      !!isDemoUnit,
      canApply,
      currentApplicationUserStatus,
      blocked,
    );
    const href = getHref(
      homeId,
      user?.email,
      currentApplication,
      currentApplicationUserStatus,
    );

    const isLoadingData = isLoadingUser || isLoadingApplications;
    const isApplyDisabled = isDemoUnit || !canApply || isLoadingData;

    return (
      <Link
        ref={ref}
        variant="solid"
        size={size}
        href={href}
        disabled={!isClientReady || isApplyDisabled}
        className={clsx('p-sm text-center', className)}
      >
        {!isClientReady ? (
          <IconSpinner width={40} />
        ) : isLoadingData ? (
          <IconSpinner width={40} />
        ) : (
          label
        )}
      </Link>
    );
  },
);
